import { applyManaGain, sigilsFromLifetime } from "../economy-rules";
import { FfaEvent } from "../sim-events";
import type { ManaOrb, SimEntity, UnitRole, WorldState } from "./types";
import { C, dist, emit } from "./shared";

const COLLECTOR_ROLES = new Set<UnitRole>([
  "master",
  "guardian",
  "soldier",
  "raider",
  "chaser",
  "hero",
]);

function canCollect(role: UnitRole): boolean {
  return COLLECTOR_ROLES.has(role);
}

function nearestCollector(
  world: WorldState,
  ox: number,
  oy: number,
  radius: number,
): SimEntity | null {
  let best: SimEntity | null = null;
  let bestD = radius;
  for (const e of world.entities) {
    if (!canCollect(e.role)) continue;
    const team = world.teams[e.teamId];
    if (!team?.alive) continue;
    const d = dist(e.x, e.y, ox, oy);
    if (d <= bestD) {
      bestD = d;
      best = e;
    }
  }
  return best;
}

export function tickCollect(world: WorldState, dt: number): void {
  void dt;
  const remaining: ManaOrb[] = [];
  for (const orb of world.orbs) {
    if (world.time >= orb.expiresAt) continue;
    if (world.time < orb.collectableAt) {
      remaining.push(orb);
      continue;
    }
    const collector = nearestCollector(
      world,
      orb.x,
      orb.y,
      C.economy.collectRadius,
    );
    if (!collector) {
      remaining.push(orb);
      continue;
    }
    const team = world.teams[collector.teamId];
    if (!team?.alive) {
      remaining.push(orb);
      continue;
    }
    const beforeSig = sigilsFromLifetime(
      team.ledger.lifetimeEarnedMana,
      C.economy.sigilEveryMana,
    );
    team.ledger = applyManaGain(team.ledger, orb.value);
    const afterSig = sigilsFromLifetime(
      team.ledger.lifetimeEarnedMana,
      C.economy.sigilEveryMana,
    );
    team.sigils += afterSig - beforeSig;
    emit(world, {
      kind: FfaEvent.ManaCollect,
      time: world.time,
      teamId: team.id,
      x: orb.x,
      y: orb.y,
      value: orb.value,
      jackpot: orb.value >= 20,
    });
  }
  world.orbs = remaining;
}

export function tickNaturalMana(world: WorldState, dt: number): void {
  world.naturalManaCd -= dt;
  if (world.naturalManaCd > 0) return;
  world.naturalManaCd = C.economy.naturalManaIntervalSec;
  const n =
    C.economy.naturalClusterMin +
    Math.floor(
      Math.random() *
        (C.economy.naturalClusterMax - C.economy.naturalClusterMin + 1),
    );
  const cx =
    C.map.outerMargin +
    Math.random() * (world.mapW - C.map.outerMargin * 2);
  const cy =
    C.map.outerMargin +
    Math.random() * (world.mapH - C.map.outerMargin * 2);
  for (let i = 0; i < n; i++) {
    world.orbs.push({
      id: world.nextId++,
      x: cx + (Math.random() - 0.5) * 90,
      y: cy + (Math.random() - 0.5) * 90,
      value: C.economy.naturalOrbValue,
      collectableAt: world.time,
      expiresAt: world.time + C.economy.dropExpireSec,
    });
  }
}
